import React, { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Form, Spinner } from 'react-bootstrap';
import { Search, Star, AlertTriangle, Copy, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { GlowpickProduct } from '../../../../models/Glowpick';
import type { CPSearchList, CoupangProduct } from '../../../../models/Coupang';
import { ScrapingStatus, ShippingType } from '../../../../models/Coupang';
import { cpGpMatchClient } from '../../../../infrastructure/CpGpMatchClient';
import { glowpickClient } from '../../../../infrastructure/GlowpickClient';
import { convertToLocalDate } from '../../../../utils/dateUtils';
import { ROUTES } from '../../../../routes/routes';
import { useAuth } from '../../../../providers/AuthProvider';
import './CoupangProductsTable.css';

// Utility function to format prices with backward compatibility
const formatPrice = (product: CoupangProduct): string => {
  // New structure: priceInfo.salePrice is a string like "14,540원"
  if (product.priceInfo?.salePrice) {
    return product.priceInfo.salePrice;
  }

  // Legacy structure: salePrice or unitPrice as numbers
  const legacyProduct = product as any;
  if (legacyProduct.salePrice) {
    return legacyProduct.salePrice.toLocaleString() + '원';
  }
  if (legacyProduct.unitPrice) {
    return legacyProduct.unitPrice.toLocaleString() + '원';
  }

  return 'N/A';
};

interface CoupangProductsTableProps {
  selectedGlowpickProduct: GlowpickProduct | null;
  cpSearchList: CPSearchList;
  loading?: boolean;
  onShowToast?: (message: string) => void;
  onMetadataUpdate?: (productId: string, metadata: Record<string, any>) => void;
  onMatchSuccess?: () => void;
}

export const CoupangProductsTable: React.FC<CoupangProductsTableProps> = ({
  selectedGlowpickProduct,
  cpSearchList,
  loading = false,
  onShowToast,
  onMetadataUpdate,
  onMatchSuccess,
}) => {
  const { user } = useAuth();
  const [matchedProducts, setMatchedProducts] = useState<Set<string>>(new Set());
  const [matchedProductsData, setMatchedProductsData] = useState<Map<string, any>>(new Map());
  const [processingMatches, setProcessingMatches] = useState<Set<string>>(new Set());
  const [failedMatches, setFailedMatches] = useState<Set<string>>(new Set());

  // Helper function to get operator info from metadata
  const getOperatorInfo = useCallback(
    (product: any) => {
      if (!selectedGlowpickProduct?.metadata?.['cpProducts']) {
        return null;
      }

      const cpUniqueId = product.cpUniqueId || `${product.cpProductId}-${product.cpItemId}`;
      const matchedProduct = selectedGlowpickProduct.metadata['cpProducts'].find(
        (cp: any) => cp.cpUniqueId === cpUniqueId,
      );

      if (matchedProduct) {
        return {
          operator: matchedProduct.operator || 'Unknown',
          operatedAt: matchedProduct.operatedAt,
          localDate: matchedProduct.operatedAt ? convertToLocalDate(matchedProduct.operatedAt) : null,
        };
      }

      return null;
    },
    [selectedGlowpickProduct],
  );

  // Helper function to get scraping status from matched products data
  const getScrapingStatus = useCallback(
    (product: any): ScrapingStatus | null => {
      const matchKey = `${product.cpProductId}-${product.cpItemId}`;
      const matchData = matchedProductsData.get(matchKey);

      if (matchData?.scrapingStatus) {
        return matchData.scrapingStatus;
      }

      return null;
    },
    [matchedProductsData],
  );

  // Helper function to render status badge based on scraping status
  const renderStatusBadge = useCallback(
    (product: any, scrapingStatus: ScrapingStatus | null) => {
      if (!selectedGlowpickProduct) {
        return null;
      }

      // Show "Go to Matched" link only for COMPLETED status
      if (scrapingStatus === ScrapingStatus.COMPLETED || String(scrapingStatus).toUpperCase() === 'COMPLETED') {
        return (
          <Link
            to={ROUTES.sourcing.matchedDetail(product.cpProductId, product.cpItemId)}
            state={{
              productData: {
                cpProductId: product.cpProductId,
                cpItemId: product.cpItemId,
                gpProductId: selectedGlowpickProduct.productId,
                keyword: `${selectedGlowpickProduct.brand} ${selectedGlowpickProduct.name}`,
                cpDetailedProduct: product.cpSearchedProduct,
                gpDetailedProduct: selectedGlowpickProduct,
                createdAt: new Date().toISOString(),
              },
            }}
            className="text-decoration-none"
            onClick={(e) => e.stopPropagation()}
          >
            <Badge
              bg="success"
              className="d-flex align-items-center px-2 py-1"
              style={{
                fontSize: '0.7rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                height: '24px',
                lineHeight: '1',
              }}
            >
              Go to Matched ↗
            </Badge>
          </Link>
        );
      }

      // Show FAILED badge for failed scraping (handle both enum and string values)
      if (scrapingStatus === ScrapingStatus.FAILED || String(scrapingStatus).toUpperCase() === 'FAILED') {
        return (
          <Badge
            bg="danger"
            className="d-flex align-items-center gap-1 px-2 py-1"
            style={{
              fontSize: '0.7rem',
              fontWeight: 'bold',
              height: '24px',
              lineHeight: '1',
            }}
          >
            <AlertTriangle size={10} />
            FAILED
          </Badge>
        );
      }

      // Show status badge for PENDING or IN_PROGRESS
      const statusStr = String(scrapingStatus).toUpperCase();
      if (
        scrapingStatus === ScrapingStatus.PENDING ||
        scrapingStatus === ScrapingStatus.IN_PROGRESS ||
        statusStr === 'PENDING' ||
        statusStr === 'IN_PROGRESS'
      ) {
        return (
          <Badge
            bg="secondary"
            className="d-flex align-items-center px-2 py-1"
            style={{
              fontSize: '0.7rem',
              fontWeight: 'bold',
              height: '24px',
              lineHeight: '1',
            }}
          >
            {scrapingStatus}
          </Badge>
        );
      }

      // For null/undefined status, show nothing
      return null;
    },
    [selectedGlowpickProduct],
  );

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onShowToast?.(`Product ID copied: ${text}`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      onShowToast?.('Failed to copy product ID');
    }
  };

  const loadExistingMatches = useCallback(async () => {
    if (!selectedGlowpickProduct) {
      return;
    }

    try {
      const result = await cpGpMatchClient.getMatchesForGpProduct(selectedGlowpickProduct.productId);
      const matchedCpUniqueId = new Set(result.matches.map((match: any) => `${match.cpProductId}-${match.cpItemId}`));

      // Store the full match data including scrapingStatus
      const matchDataMap = new Map();
      result.matches.forEach((match: any) => {
        const matchKey = `${match.cpProductId}-${match.cpItemId}`;
        matchDataMap.set(matchKey, match);
      });

      setMatchedProducts(matchedCpUniqueId);
      setMatchedProductsData(matchDataMap);
      // Clear failed matches when reloading existing matches
      setFailedMatches(new Set());
    } catch (error) {
      console.error('Failed to load existing matches:', error);
    }
  }, [selectedGlowpickProduct]);

  const handleMatchToggle = useCallback(
    async (product: any, isCurrentlyMatched: boolean) => {
      if (!selectedGlowpickProduct) {
        return;
      }

      // Capture the current product context to avoid closure issues
      const productContextForThisOperation = selectedGlowpickProduct;
      const matchKey = `${product.cpProductId}-${product.cpItemId}`;

      // Clear any previous failed state
      setFailedMatches((prev) => {
        const newSet = new Set(prev);
        newSet.delete(matchKey);
        return newSet;
      });

      setProcessingMatches((prev) => new Set(prev).add(matchKey));

      try {
        if (isCurrentlyMatched) {
          // Delete the match with optimistic UI updates
          const cpUniqueId = `${product.cpProductId}-${product.cpItemId}`;

          // Step 1: Update local UI immediately for responsive experience
          const existingMetadata = productContextForThisOperation.metadata || {};
          const existingCpProducts = existingMetadata['cpProducts'] || [];

          // Remove the deleted product from metadata
          const newCpProducts = existingCpProducts.filter((cp: any) => cp.cpUniqueId !== cpUniqueId);
          const updatedMetadata = {
            cpProducts: newCpProducts,
            notFoundInCoupang: false,
          };

          // Update local UI immediately
          console.info('🚀 Updating local UI immediately (delete)...');
          setMatchedProducts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(matchKey);
            return newSet;
          });

          // Update metadata for UI if this product is still selected
          if (selectedGlowpickProduct?.productId === productContextForThisOperation.productId) {
            onMetadataUpdate?.(productContextForThisOperation.productId, updatedMetadata);
          }

          try {
            // Step 2: Delete the match from server
            const result = await cpGpMatchClient.deleteMatch(product.cpProductId, product.cpItemId);
            if (result.success) {
              console.info('✅ Match deleted from server successfully');

              // Step 3: Sync metadata to server
              const metadataResult = await glowpickClient.updateProductMetadata(
                productContextForThisOperation.productId,
                updatedMetadata,
              );

              if (metadataResult.success) {
                console.info('✅ Metadata synced to server successfully (delete)');
                // Call onMatchSuccess only when both operations are successful
                // This ensures silentRefresh() gets the updated data from server
                onMatchSuccess?.();
              } else {
                console.error('❌ Failed to sync metadata to server (delete):', metadataResult.message);
                // Metadata sync failed, but match is deleted - still call onMatchSuccess
                // The main list will be updated by silentRefresh
                onMatchSuccess?.();
              }
            } else {
              console.error('❌ Failed to delete match from server:', result.message);
              // Revert local state on server failure
              setMatchedProducts((prev) => new Set(prev).add(matchKey));
              const revertedMetadata = {
                cpProducts: existingCpProducts,
                notFoundInCoupang: false,
              };
              // Revert metadata for UI if this product is still selected
              if (selectedGlowpickProduct?.productId === productContextForThisOperation.productId) {
                onMetadataUpdate?.(productContextForThisOperation.productId, revertedMetadata);
              }
              setFailedMatches((prev) => new Set(prev).add(matchKey));
            }
          } catch (error) {
            console.error('❌ Error in match deletion process:', error);
            // Revert local state on error
            setMatchedProducts((prev) => new Set(prev).add(matchKey));
            const revertedMetadata = {
              cpProducts: existingCpProducts,
              notFoundInCoupang: false,
            };
            // Revert metadata for UI if this product is still selected
            if (selectedGlowpickProduct?.productId === productContextForThisOperation.productId) {
              onMetadataUpdate?.(productContextForThisOperation.productId, revertedMetadata);
            }
            setFailedMatches((prev) => new Set(prev).add(matchKey));
          }
        } else {
          // Create the match with optimistic UI updates
          const operator = user?.name || 'Unknown User';
          const operatedAt = new Date().toISOString();
          const cpUniqueId = `${product.cpProductId}-${product.cpItemId}`;

          // Step 1: Update local UI immediately for responsive experience
          const existingMetadata = productContextForThisOperation.metadata || {};
          const existingCpProducts = existingMetadata['cpProducts'] || [];

          // Add new operator info if not already exists
          const newCpProducts = [...existingCpProducts];
          if (!newCpProducts.some((cp: any) => cp.cpUniqueId === cpUniqueId)) {
            newCpProducts.push({ cpUniqueId, operator, operatedAt });
          }

          const updatedMetadata = {
            cpProducts: newCpProducts,
            notFoundInCoupang: false,
          };

          // Update local Glowpick table immediately
          console.info('🚀 Updating local Glowpick table immediately...');
          setMatchedProducts((prev) => new Set(prev).add(matchKey));

          // Only update metadata for UI if this product is still selected
          // This prevents focus jumps when user has switched to a different product
          if (selectedGlowpickProduct?.productId === productContextForThisOperation.productId) {
            onMetadataUpdate?.(productContextForThisOperation.productId, updatedMetadata);
          }

          try {
            // Step 2: Update server metadata with operator info
            console.info('🔄 Syncing metadata to server...');
            const metadataResult = await glowpickClient.updateProductMetadata(
              productContextForThisOperation.productId,
              updatedMetadata,
            );

            if (!metadataResult.success) {
              console.error('❌ Failed to sync metadata to server:', metadataResult.message);
              // Revert local state on metadata failure
              setMatchedProducts((prev) => {
                const newSet = new Set(prev);
                newSet.delete(matchKey);
                return newSet;
              });
              const revertedMetadata = {
                cpProducts: existingCpProducts,
                notFoundInCoupang: false,
              };
              // Only update metadata for UI if this product is still selected
              if (selectedGlowpickProduct?.productId === productContextForThisOperation.productId) {
                onMetadataUpdate?.(productContextForThisOperation.productId, revertedMetadata);
              }
              setFailedMatches((prev) => new Set(prev).add(matchKey));
              return;
            }

            console.info('✅ Metadata synced to server successfully');

            // Step 3: Create the match - operator info is already safely stored
            const matchData = {
              cpProductId: product.cpProductId,
              cpItemId: product.cpItemId,
              gpProductId: productContextForThisOperation.productId,
              keyword: `${productContextForThisOperation.brand} ${productContextForThisOperation.name}`,
              thumbnailImageUrl: product.cpSearchedProduct.thumbnailImageUrl,
              productPageUrl: product.cpSearchedProduct.productPageUrl,
            };

            const result = await cpGpMatchClient.createMatch(matchData);
            if (result.success) {
              console.info('✅ Match created successfully - all operations complete');
              onMatchSuccess?.();
            } else {
              console.error('❌ Failed to create match:', result.message);
              // Note: We don't revert metadata since it's already saved and the operator action should be tracked
              setFailedMatches((prev) => new Set(prev).add(matchKey));
            }
          } catch (error) {
            console.error('❌ Error in match creation process:', error);
            setFailedMatches((prev) => new Set(prev).add(matchKey));
          }
        }
      } catch (error) {
        console.error('❌ Error toggling match:', error);
        // Set failed state for any exception
        setFailedMatches((prev) => new Set(prev).add(matchKey));
      } finally {
        setProcessingMatches((prev) => {
          const newSet = new Set(prev);
          newSet.delete(matchKey);
          return newSet;
        });
      }
    },
    [selectedGlowpickProduct, user, onMetadataUpdate, onMatchSuccess],
  );

  // Note: Metadata updates are now handled synchronously before createMatch calls,
  // so no cleanup or batching is needed

  // Note: Metadata updates are now handled synchronously when deleting matches

  useEffect(() => {
    if (selectedGlowpickProduct) {
      loadExistingMatches();
    } else {
      setMatchedProducts(new Set());
      setMatchedProductsData(new Map());
      setFailedMatches(new Set());
    }
  }, [selectedGlowpickProduct, cpSearchList, loadExistingMatches]);

  // Only show "select a product" message if no Glowpick product AND no Coupang products
  if (!selectedGlowpickProduct && cpSearchList.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-center text-muted">
          <Search size={48} className="mb-3 opacity-50" />
          <h5>Select a product in the left panel</h5>
          <p>Click on a product to see matching Coupang products</p>
        </div>
      </div>
    );
  }

  // Show loading overlay when fetching Coupang search results
  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="text-muted">Loading Coupang search results...</h5>
          <p className="text-muted">
            {selectedGlowpickProduct
              ? `Searching for Glowpick brand: ${selectedGlowpickProduct.brand}, name: ${selectedGlowpickProduct.name}`
              : 'Searching for matching products...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Content area - conditional based on results */}
      {cpSearchList.length === 0 ? (
        /* No results content */
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
          <div className="text-center text-muted">
            <Search size={48} className="mb-3 opacity-50" />
            <h5>0 results found</h5>
            <p>
              {selectedGlowpickProduct
                ? `No Coupang products found for "${selectedGlowpickProduct.brand} ${selectedGlowpickProduct.name}"`
                : 'No Coupang products found for the selected product'}
            </p>
            <p className="text-info">
              Click <strong>Scrape</strong> above to trigger a new search.
            </p>
          </div>
        </div>
      ) : (
        /* Results grid */
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: '0.6rem',
            margin: '0.6rem',
          }}
        >
          {cpSearchList.map((product) => {
            const matchKey = product.cpUniqueId || `${product.cpProductId}-${product.cpItemId}`;
            const isMatched = matchedProducts.has(matchKey);
            const isProcessing = processingMatches.has(matchKey);
            const isFailed = failedMatches.has(matchKey);
            const hasRanking =
              product.cpSearchedProduct.ranking &&
              Number.isInteger(product.cpSearchedProduct.ranking) &&
              product.cpSearchedProduct.ranking >= 0;

            // Allow multiple selection - only disable the current product when it's being processed

            // Get operator information from metadata
            const operatorInfo = getOperatorInfo(product);
            const scrapingStatus = getScrapingStatus(product);

            return (
              <div key={matchKey} className="position-relative">
                <Card
                  className={`h-100 position-relative ${isMatched ? 'border-success' : ''} ${isProcessing ? 'opacity-75' : ''}`}
                  style={{
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: isMatched ? '0 0 0 2px rgba(40,167,69,0.25)' : 'none',
                    backgroundColor: isMatched ? '#f8fff9' : 'white',
                  }}
                  onMouseEnter={(e) => {
                    if (!isMatched && !isProcessing) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMatched && !isProcessing) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  {/* Updated At - positioned at top right */}
                  <div
                    className="d-flex justify-content-end"
                    style={{
                      top: '0px',
                      right: '0px',
                      zIndex: 10,
                    }}
                  >
                    <small className="text-muted" style={{ fontSize: '0.65rem', margin: '2px' }}>
                      {product.updatedAt ? 'Scraped: ' + convertToLocalDate(product.updatedAt) : 'N/A'}
                    </small>
                  </div>

                  {/* Product Image */}
                  <div className="position-relative" style={{ marginTop: '0px', padding: 0 }}>
                    {/* Updated At */}

                    <img
                      src={product.cpSearchedProduct.thumbnailImageUrl}
                      alt={product.cpSearchedProduct.name}
                      style={{
                        height: '230px',
                        width: '100%',
                        objectFit: 'contain',
                        backgroundColor: 'white',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                      }}
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/230x230?text=Image+Not+Available';
                      }}
                    />
                    {/* Coupang Overlay - covers the image */}
                    <a
                      href={
                        product.cpSearchedProduct.productPageUrl ||
                        `https://www.coupang.com/vp/products/${product.cpProductId}?itemId=${product.cpItemId}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-decoration-none"
                      style={{
                        background: 'rgba(0,0,0,0.45)',
                        color: 'white',
                        fontSize: '0.7rem',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        borderRadius: '0.3rem',
                        zIndex: 11,
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                      }}
                    >
                      Go to Coupang ↗
                    </a>

                    {/* Ranking Badge - Vertical Red Ribbon style */}
                    {hasRanking && (
                      <div className="ranking-ribbon">
                        <div className="ribbon-content" title={`Ranking: ${product.cpSearchedProduct.ranking}`}>
                          {product.cpSearchedProduct.ranking}
                          <div className="ribbon-tail" />
                          <div className="ribbon-shadow" />
                        </div>
                      </div>
                    )}
                  </div>

                  <Card.Body className="p-2">
                    {/* Product IDs */}
                    <div
                      className="d-flex align-items-center justify-content-center p-2 gap-1"
                      style={{
                        fontSize: '0.7rem',
                        color: '#6c757d',
                      }}
                    >
                      <span>
                        {product.cpProductId}-{product.cpItemId}
                      </span>
                      <button
                        className="btn btn-sm p-0 border-0 bg-transparent"
                        style={{
                          color: '#6c757d',
                          fontSize: '0.7rem',
                          cursor: 'pointer',
                          opacity: 0.7,
                          transition: 'opacity 0.2s',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(`${product.cpProductId}-${product.cpItemId}`);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '0.7';
                        }}
                        title="Copy product ID"
                      >
                        <Copy size={12} />
                      </button>
                    </div>

                    {/* Brand */}
                    <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                      {product.cpSearchedProduct.brand}
                    </div>

                    {/* Product Name - Full Text */}
                    <div
                      className="fw-normal mb-1"
                      style={{
                        fontSize: '0.85rem',
                        lineHeight: '1.2',
                        height: '3.1rem',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                      title={product.cpSearchedProduct.name}
                    >
                      {product.cpSearchedProduct.name}
                    </div>

                    {/* Rating and Reviews */}
                    {product.cpSearchedProduct.rating && product.cpSearchedProduct.rating > 0 && (
                      <div className="d-flex align-items-center mb-1">
                        <div className="d-flex align-items-center me-1">
                          <Star size={12} className="text-warning me-1" fill="currentColor" />
                          <span style={{ fontSize: '0.75rem' }}>{product.cpSearchedProduct.rating}</span>
                        </div>
                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                          ({product.cpSearchedProduct.reviews?.toLocaleString()})
                        </span>
                      </div>
                    )}

                    {/* Price with Shipping Icon */}
                    <div className="mt-auto">
                      <div className="d-flex flex-column">
                        {/* Product Options/Size */}
                        {product.cpSearchedProduct.options && product.cpSearchedProduct.options.length > 0 && (
                          <div className="mb-1">
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                              {product.cpSearchedProduct.options[0]?.itemName}
                            </span>
                          </div>
                        )}

                        {/* Price and Unit Price */}
                        <div className="d-flex flex-column mb-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex flex-column">
                              <span className="text-danger fw-bold" style={{ fontSize: '0.9rem' }}>
                                {formatPrice(product.cpSearchedProduct)}
                                {product.cpSearchedProduct.priceInfo?.unitPrice && (
                                  <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                    {product.cpSearchedProduct.priceInfo.unitPrice}
                                  </span>
                                )}
                              </span>
                              {(product.cpSearchedProduct.priceInfo?.discountRate ||
                                (product.cpSearchedProduct as any).discountRate) && (
                                <span className="text-success" style={{ fontSize: '0.6rem' }}>
                                  {product.cpSearchedProduct.priceInfo?.discountRate ||
                                    (product.cpSearchedProduct as any).discountRate}{' '}
                                  할인
                                </span>
                              )}
                            </div>

                            {/* Shipping Icon */}
                            {(product.cpSearchedProduct.shippingInfo?.shippingType ||
                              product.cpSearchedProduct.shipping) && (
                              <div className="d-flex align-items-center ms-2">
                                {product.cpSearchedProduct.shippingInfo?.shippingType ? (
                                  // New shippingInfo structure
                                  <>
                                    {product.cpSearchedProduct.shippingInfo.shippingType === ShippingType.ROCKET && (
                                      <img
                                        src="/assets/rocket-logo.png"
                                        alt="Rocket"
                                        style={{ width: 'auto', height: '14px' }}
                                      />
                                    )}
                                    {product.cpSearchedProduct.shippingInfo.shippingType ===
                                      ShippingType.SELLER_ROCKET && (
                                      <img
                                        src="/assets/seller-rocket-logo.png"
                                        alt="Seller Rocket"
                                        style={{ height: '14px', width: 'auto' }}
                                        title="판매자로켓"
                                      />
                                    )}
                                  </>
                                ) : (
                                  // Legacy shipping field
                                  <>
                                    {product.cpSearchedProduct.shipping === 'Rocket' && (
                                      <img
                                        src="/assets/rocket-logo.png"
                                        alt="Rocket"
                                        style={{ width: 'auto', height: '14px' }}
                                      />
                                    )}
                                    {product.cpSearchedProduct.shipping === 'Seller Rocket' && (
                                      <img
                                        src="/assets/seller-rocket-logo.png"
                                        alt="Seller Rocket"
                                        style={{ height: '14px', width: 'auto' }}
                                        title="판매자로켓"
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="d-flex flex-column" style={{ fontSize: '0.65rem' }}>
                          {product.cpSearchedProduct.shippingInfo?.deliveryDescription && (
                            <span className="text-success" style={{ fontSize: '0.7rem' }}>
                              {product.cpSearchedProduct.shippingInfo.deliveryDescription}
                            </span>
                          )}
                          {product.cpSearchedProduct.shippingInfo?.shippingType === ShippingType.SELLER_DELIVERY && (
                            <span className="text-muted">무료배송</span>
                          )}
                          {product.cpSearchedProduct.shipping === 'Standard' && (
                            <span className="text-success fw-bold">무료배송</span>
                          )}
                          {product.cpSearchedProduct.shipping !== 'Rocket' &&
                            product.cpSearchedProduct.shipping !== 'Seller Rocket' &&
                            product.cpSearchedProduct.shipping !== 'Standard' &&
                            product.cpSearchedProduct.shipping && (
                              <span className="text-muted">{product.cpSearchedProduct.shipping}</span>
                            )}
                        </div>
                      </div>
                    </div>

                    {/* Bottom section with matching info - always show for interaction */}
                    <div className="mt-2 pt-2 border-top">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          {/* Checkbox with status indicator */}
                          <div className="d-flex align-items-center" style={{ height: '24px' }}>
                            <Form.Check
                              type="checkbox"
                              className="round-checkbox"
                              checked={isMatched}
                              disabled={isProcessing}
                              onChange={() => handleMatchToggle(product, isMatched)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                fontSize: '0.7rem',
                                transform: 'scale(0.9)',
                                margin: 0,
                              }}
                              title={
                                isProcessing
                                  ? 'Processing...'
                                  : isMatched
                                    ? 'Uncheck to remove match'
                                    : 'Check to match with Glowpick product'
                              }
                            />
                          </div>

                          {/* Status badges */}
                          {isProcessing && (
                            <Badge
                              bg="warning"
                              className="d-flex align-items-center gap-1 px-2 py-1"
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                height: '24px',
                                lineHeight: '1',
                              }}
                            >
                              <Spinner size="sm" style={{ width: '10px', height: '10px' }} />
                              MATCHING
                            </Badge>
                          )}

                          {/* Matched status badges */}
                          {isMatched && !isProcessing && (
                            <>
                              {renderStatusBadge(product, scrapingStatus)}

                              {/* Operator info - only show for COMPLETED status */}
                              {scrapingStatus === ScrapingStatus.COMPLETED && operatorInfo && (
                                <div className="d-flex align-items-center gap-1">
                                  <User size={12} className="text-muted" />
                                  <div className="d-flex flex-column" style={{ fontSize: '0.65rem' }}>
                                    <span style={{ fontWeight: 'bold', lineHeight: '1' }}>{operatorInfo.operator}</span>
                                    {operatorInfo.localDate && (
                                      <span style={{ color: '#6c757d', lineHeight: '1' }}>
                                        {operatorInfo.localDate}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {isFailed && !isProcessing && !isMatched && (
                            <Badge
                              bg="danger"
                              className="d-flex align-items-center gap-1 px-2 py-1"
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                height: '24px',
                                lineHeight: '1',
                              }}
                              title="Click to retry matching"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMatchToggle(product, false);
                              }}
                            >
                              <AlertTriangle size={10} />
                              FAILED
                            </Badge>
                          )}

                          {!isMatched && !isProcessing && !isFailed && (
                            <div className="d-flex align-items-center" style={{ height: '24px' }}>
                              <span className="text-muted" style={{ fontSize: '0.7rem', lineHeight: '1' }}>
                                Click to match
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
